import React, { useState, useEffect } from 'react';
import { PlannedConcept, PlanningCategory } from '../../types';
import { createPlannedConcept, updatePlannedConcept, deletePlannedConcept } from '../../services/planningService';
import { Plus, Trash2, Tag, Repeat, Edit2, AlertCircle, Settings2 } from 'lucide-react';
import { ConceptCategoriesManager } from './ConceptCategoriesManager';

export const ConceptsView: React.FC<{ concepts: PlannedConcept[], categories: PlanningCategory[], userId: string }> = ({ concepts, categories, userId }) => {
  const [showForm, setShowForm] = useState(false);
  const [showCatManager, setShowCatManager] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [type, setType] = useState<'INCOME' | 'EXPENSE'>('EXPENSE');
  const [category, setCategory] = useState<string>('ORDINARY');

  useEffect(() => {
    // If we have categories and it's a new concept, default to the first one or ORDINARY if it exists
    if (!editingId && !showForm) {
      if (categories.length > 0) {
        setCategory(categories[0].id);
      } else {
        setCategory('ORDINARY');
      }
    }
  }, [categories, editingId, showForm]);
  const [isAccumulative, setIsAccumulative] = useState(false);
  const [recurrence, setRecurrence] = useState<'NONE' | 'MONTHLY' | 'BIMONTHLY' | 'QUARTERLY' | 'SEMIANNUAL' | 'ANNUAL'>('MONTHLY');
  const [defaultAmount, setDefaultAmount] = useState('');
  const [limitOccurrences, setLimitOccurrences] = useState(false);
  const [maxOccurrences, setMaxOccurrences] = useState('3');
  const [hasStartPeriod, setHasStartPeriod] = useState(false);
  const [startPeriod, setStartPeriod] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [hasDefaultDay, setHasDefaultDay] = useState(false);
  const [defaultDay, setDefaultDay] = useState('1');

  const [searchTerm, setSearchTerm] = useState('');

  const getCategoryName = (catId: string) => {
    // 1. Intentar buscar por ID exacto (lo más seguro ahora con IDs fijos)
    let found = categories.find(c => c.id === catId);
    if (found) return found.name;

    // 2. Intentar buscar por Nombre (por si el concepto guardó el nombre personalizado)
    found = categories.find(c => c.name === catId);
    if (found) return found.name;

    // 3. Casos especiales legacy
    if (catId === 'ORDINARY') {
      // Si no hay categoría con ID ORDINARY, pero hay una que se llama "Ordinario" o similar, o simplemente es la primera
      return 'Ordinario';
    }
    if (catId === 'EXTRAORDINARY') {
      return 'Extraordinario';
    }

    return catId;
  };

  const handleOpenForm = (c?: PlannedConcept) => {
    if (c) {
      setEditingId(c.id);
      setTitle(c.title);
      setType(c.type);
      setCategory(c.category);
      setIsAccumulative(c.isAccumulative);
      setRecurrence(c.recurrence);
      setDefaultAmount(c.defaultAmount.toString());
      if (c.maxOccurrences && c.maxOccurrences > 0) {
        setLimitOccurrences(true);
        setMaxOccurrences(c.maxOccurrences.toString());
      } else {
        setLimitOccurrences(false);
        setMaxOccurrences('3');
      }
      if (c.startPeriod) {
        setHasStartPeriod(true);
        setStartPeriod(c.startPeriod);
      } else {
        setHasStartPeriod(false);
        const d = new Date();
        setStartPeriod(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
      }
      if (c.defaultDay) {
        setHasDefaultDay(true);
        setDefaultDay(c.defaultDay.toString());
      } else {
        setHasDefaultDay(false);
        setDefaultDay('1');
      }
    } else {
      setEditingId(null);
      setTitle('');
      setType('EXPENSE');
      setCategory(categories.length > 0 ? categories[0].id : 'ORDINARY');
      setIsAccumulative(false);
      setRecurrence('MONTHLY');
      setDefaultAmount('');
      setLimitOccurrences(false);
      setMaxOccurrences('3');
      setHasStartPeriod(false);
      const d = new Date();
      setStartPeriod(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
      setHasDefaultDay(false);
      setDefaultDay('1');
    }
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingId(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title) return;
    
    const conceptData = {
      title,
      type,
      category,
      isAccumulative,
      recurrence,
      defaultAmount: Number(defaultAmount) || 0,
      maxOccurrences: limitOccurrences ? (Number(maxOccurrences) || 0) : null,
      startPeriod: hasStartPeriod ? startPeriod : null,
      defaultDay: hasDefaultDay ? (Number(defaultDay) || null) : null
    };

    if (editingId) {
      await updatePlannedConcept(userId, editingId, conceptData);
    } else {
      await createPlannedConcept(userId, conceptData);
    }
    
    handleCloseForm();
  };

  const handleDeleteClick = (id: string) => {
    if (deletingId === id) {
      deletePlannedConcept(userId, id);
      setDeletingId(null);
    } else {
      setDeletingId(id);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG' }).format(amount);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <h2 className="text-lg font-bold text-white tracking-tight flex items-center gap-2">
          <Settings2 size={20} className="text-zinc-500" />
          Conceptos de Planificación
        </h2>
        <div className="flex items-center gap-4 w-full md:w-auto">
          <input
            type="text"
            placeholder="Buscar concepto..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full md:w-64 bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-2 text-white focus:outline-none focus:border-indigo-500 transition-colors"
          />
          <button
            onClick={() => setShowCatManager(!showCatManager)}
            className={`p-2 rounded-xl border transition-colors ${showCatManager ? 'bg-zinc-800 border-zinc-600 text-white' : 'bg-zinc-900 border-zinc-800 text-zinc-500 hover:text-zinc-300'}`}
            title="Gestionar Categorías"
          >
            <Tag size={20} />
          </button>
          <button 
            onClick={() => handleOpenForm()}
            disabled={showForm}
            className="flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 disabled:bg-indigo-500/50 text-black px-4 py-2 rounded-xl font-medium transition-colors w-full md:w-auto justify-center"
          >
            <Plus size={18} />
            <span className="hidden md:inline">Nuevo Concepto</span>
            <span className="md:hidden">Nuevo</span>
          </button>
        </div>
      </div>

      {showCatManager && (
        <ConceptCategoriesManager categories={categories} userId={userId} />
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-zinc-800/50 p-6 rounded-2xl border border-zinc-700/50 space-y-4">
          <div className="flex items-center gap-2 mb-2 border-b border-zinc-700/50 pb-4">
            <Edit2 size={18} className="text-indigo-400" />
            <h3 className="font-bold text-white tracking-tight">
              {editingId ? 'Editar Concepto' : 'Crear Nuevo Concepto'}
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-zinc-400 mb-2">TÍTULO</label>
              <input
                required
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-indigo-500/50 outline-none"
                placeholder="Ej. Alquiler, Salario..."
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-zinc-400 mb-2">MONTO POR DEFECTO</label>
              <input
                type="number"
                value={defaultAmount}
                onChange={e => setDefaultAmount(e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-indigo-500/50 outline-none"
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-zinc-400 mb-2">TIPO</label>
              <select
                value={type}
                onChange={e => setType(e.target.value as 'INCOME' | 'EXPENSE')}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-3 text-white outline-none"
              >
                <option value="EXPENSE">Gasto / Egreso</option>
                <option value="INCOME">Ingreso</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-zinc-400 mb-2">CATEGORÍA</label>
              <select
                value={category}
                onChange={e => setCategory(e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-3 text-white outline-none"
              >
                {categories.length > 0 ? (
                  categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))
                ) : (
                  <>
                    <option value="ORDINARY">Ordinario</option>
                    <option value="EXTRAORDINARY">Extraordinario</option>
                  </>
                )}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-zinc-400 mb-2">RECURRENCIA</label>
              <select
                value={recurrence}
                onChange={e => setRecurrence(e.target.value as any)}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-3 text-white outline-none"
              >
                <option value="NONE">Ninguna (Solo una vez)</option>
                <option value="MONTHLY">Mensual</option>
                <option value="BIMONTHLY">Bimensual</option>
                <option value="QUARTERLY">Trimestral</option>
                <option value="SEMIANNUAL">Semestral</option>
                <option value="ANNUAL">Anual</option>
              </select>
            </div>
            <div className="flex flex-col justify-center translate-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={isAccumulative}
                    onChange={e => setIsAccumulative(e.target.checked)}
                    className="sr-only"
                  />
                  <div className={`block w-10 h-6 rounded-full transition-colors ${isAccumulative ? 'bg-indigo-500' : 'bg-zinc-700'}`}></div>
                  <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${isAccumulative ? 'translate-x-4' : ''}`}></div>
                </div>
                <span className="text-sm font-medium text-zinc-300">Es Acumulativo</span>
              </label>
              <p className="text-xs text-zinc-500 mt-1">Los acumulativos suman registros existentes sin planificar uno concreto.</p>
            </div>
            
            <div className="flex flex-col justify-center pt-2">
              <label className="flex items-center gap-3 cursor-pointer mb-2">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={limitOccurrences}
                    onChange={e => setLimitOccurrences(e.target.checked)}
                    className="sr-only"
                  />
                  <div className={`block w-10 h-6 rounded-full transition-colors ${limitOccurrences ? 'bg-indigo-500' : 'bg-zinc-700'}`}></div>
                  <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${limitOccurrences ? 'translate-x-4' : ''}`}></div>
                </div>
                <span className="text-sm font-medium text-zinc-300">Límite de duración</span>
              </label>
              {limitOccurrences && (
                <div className="mt-2 flex items-center gap-3">
                  <input
                    type="number"
                    min="1"
                    value={maxOccurrences}
                    onChange={e => setMaxOccurrences(e.target.value)}
                    className="w-20 bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-2 text-white focus:ring-2 focus:ring-indigo-500/50 outline-none text-sm"
                  />
                  <span className="text-xs text-zinc-400">cantidad de meses/veces a planificar</span>
                </div>
              )}
            </div>

            <div className="flex flex-col justify-start pt-2">
              <label className="flex items-center gap-3 cursor-pointer mb-2">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={hasStartPeriod}
                    onChange={e => setHasStartPeriod(e.target.checked)}
                    className="sr-only"
                  />
                  <div className={`block w-10 h-6 rounded-full transition-colors ${hasStartPeriod ? 'bg-indigo-500' : 'bg-zinc-700'}`}></div>
                  <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${hasStartPeriod ? 'translate-x-4' : ''}`}></div>
                </div>
                <span className="text-sm font-medium text-zinc-300">Mes de inicio fijo</span>
              </label>
              <p className="text-[10px] text-zinc-500 mb-2">No se proyectará antes del mes seleccionado.</p>
              {hasStartPeriod && (
                <div className="mt-1">
                  <input
                    type="month"
                    value={startPeriod}
                    onChange={e => setStartPeriod(e.target.value)}
                    className="bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-2 text-white focus:ring-2 focus:ring-indigo-500/50 outline-none text-sm"
                  />
                </div>
              )}
            </div>
            <div className="flex flex-col justify-start pt-2">
              <label className="flex items-center gap-3 cursor-pointer mb-2">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={hasDefaultDay}
                    onChange={e => setHasDefaultDay(e.target.checked)}
                    className="sr-only"
                  />
                  <div className={`block w-10 h-6 rounded-full transition-colors ${hasDefaultDay ? 'bg-indigo-500' : 'bg-zinc-700'}`}></div>
                  <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${hasDefaultDay ? 'translate-x-4' : ''}`}></div>
                </div>
                <span className="text-sm font-medium text-zinc-300">Día del mes específico</span>
              </label>
              <p className="text-[10px] text-zinc-500 mb-2">Día base de pago/cobro.</p>
              {hasDefaultDay && (
                <div className="mt-1 flex items-center gap-3">
                  <input
                    type="number"
                    min="1"
                    max="31"
                    value={defaultDay}
                    onChange={e => setDefaultDay(e.target.value)}
                    className="w-20 bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-2 text-white focus:ring-2 focus:ring-indigo-500/50 outline-none text-sm"
                  />
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-zinc-700/50">
            <button 
              type="button" 
              onClick={handleCloseForm}
              className="px-4 py-2 text-sm text-zinc-400 hover:text-white transition-colors"
            >
              Cancelar
            </button>
            <button 
              type="submit"
              className="px-6 py-2 bg-indigo-500 hover:bg-indigo-600 text-black text-sm font-bold rounded-xl transition-colors min-w-[120px]"
            >
              {editingId ? 'Guardar Cambios' : 'Crear Concepto'}
            </button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {concepts
          .filter(c => c.title.toLowerCase().includes(searchTerm.toLowerCase()))
          .map(c => (
          <div key={c.id} className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl p-4 flex flex-col justify-between group">
            <div>
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-bold text-zinc-100">{c.title}</h3>
                <div className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                  c.type === 'INCOME' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'
                }`}>
                  {c.type}
                </div>
              </div>
              <div className="text-xl font-medium text-zinc-300 mb-4">
                {formatCurrency(c.defaultAmount)}
              </div>
              <div className="flex flex-wrap gap-2">
                <span className="flex items-center gap-1 text-[10px] text-zinc-400 bg-zinc-800 px-2 py-1 rounded-md">
                  <Tag size={12} /> {getCategoryName(c.category)}
                </span>
                <span className="flex items-center gap-1 text-[10px] text-zinc-400 bg-zinc-800 px-2 py-1 rounded-md">
                  <Repeat size={12} /> {c.recurrence}
                </span>
                {c.isAccumulative && (
                  <span className="text-[10px] text-indigo-400 bg-indigo-500/10 px-2 py-1 rounded-md">
                    Acumulativo
                  </span>
                )}
                {c.maxOccurrences && c.maxOccurrences > 0 ? (
                  <span className="text-[10px] text-amber-400 bg-amber-500/10 px-2 py-1 rounded-md flex items-center gap-1">
                    <AlertCircle size={10} /> Hasta {c.maxOccurrences} veces
                  </span>
                ) : null}
                {c.startPeriod && (
                  <span className="text-[10px] text-sky-400 bg-sky-500/10 px-2 py-1 rounded-md flex items-center gap-1">
                    Inicia: {c.startPeriod}
                  </span>
                )}
                {c.defaultDay && (
                   <span className="text-[10px] text-fuchsia-400 bg-fuchsia-500/10 px-2 py-1 rounded-md flex items-center gap-1">
                     Día: {c.defaultDay}
                   </span>
                )}
              </div>
            </div>
            <div className="pt-4 mt-4 border-t border-zinc-800/80 opacity-100 lg:[@media(hover:hover)]:opacity-0 lg:group-hover:opacity-100 transition-opacity flex gap-3">
              <button 
                onClick={() => handleOpenForm(c)} 
                disabled={showForm}
                className="text-indigo-400 hover:text-indigo-300 flex items-center gap-1 text-xs font-medium disabled:opacity-50"
              >
                <Edit2 size={14} /> Editar
              </button>

              {deletingId === c.id ? (
                <div className="flex items-center gap-2">
                   <button 
                    onClick={() => handleDeleteClick(c.id)} 
                    className="text-red-400 bg-red-400/10 px-2 rounded hover:bg-red-400/20 flex items-center gap-1 text-xs font-bold transition-colors"
                  >
                    ¿Seguro?
                  </button>
                  <button onClick={() => setDeletingId(null)} className="text-zinc-400 hover:text-zinc-300 text-xs text-medium">
                    Cancelar
                  </button>
                </div>
              ) : (
                <button 
                  onClick={() => handleDeleteClick(c.id)} 
                  className="text-red-400 hover:text-red-300 flex items-center gap-1 text-xs font-medium"
                >
                  <Trash2 size={14} /> Eliminar
                </button>
              )}
            </div>
          </div>
        ))}
        {concepts.length === 0 && !showForm && (
          <div className="col-span-full py-12 text-center text-zinc-500 border border-dashed border-zinc-800 rounded-2xl flex flex-col items-center justify-center gap-2">
            <AlertCircle className="text-zinc-600 mb-2" size={32} />
            <p>No hay conceptos planificados.</p>
            <button onClick={() => handleOpenForm()} className="mt-2 text-indigo-400 hover:text-indigo-300 underline font-medium">Empieza creando el primero</button>
          </div>
        )}
      </div>
    </div>
  );
};

